import { useState } from "react";
import axios from "axios";
import { X, MapPin, Building, Tags, AlertTriangle } from "lucide-react";
import { SearchableSelector, ClientSelector } from "./DevisShared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHAMPAGNE_OPTS = ["", "Avec alcool", "Sans alcool", "Non"];

export function CreateDevisModal({ vehicles, themes, lieux, lieuxCeremonie, salles, clients, authHeaders, onClose, onCreated, onRefresh }) {
  const [form, setForm] = useState({
    vehicle_id: "", montant: "", client_id: "", client_name: "", client_email: "", client_telephone: "",
    client_ville: "", date_mariage: "", lieu_evenement: "", lieu_ceremonie: "", salle_reception: "", theme_decoration: "",
    champagne: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const checkConflict = async (vehicleId, dateMarriage) => {
    if (!vehicleId || !dateMarriage) { setConflict(null); return; }
    try {
      const res = await axios.get(`${API}/devis/check-conflict?vehicle_id=${vehicleId}&date_mariage=${dateMarriage}`, { headers: authHeaders });
      setConflict(res.data.has_conflict ? res.data : null);
    } catch { setConflict(null); }
  };

  const handleVehicleChange = (vehicleId) => {
    set("vehicle_id", vehicleId);
    const v = vehicles.find(v => v.id === vehicleId);
    if (v) set("montant", v.price);
    checkConflict(vehicleId, form.date_mariage);
  };

  const handleDateChange = (date) => {
    set("date_mariage", date);
    checkConflict(form.vehicle_id, date);
  };

  const handleClientSelect = (client) => {
    set("client_id", client.id);
    set("client_name", client.nom);
    set("client_email", client.email);
    set("client_telephone", client.telephone);
    set("client_ville", client.ville || "");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const res = await axios.post(`${API}/devis?send_email=true`, form, { headers: authHeaders });
      if (res.data.email_simulated) {
        alert(`Devis ${res.data.numero} créé !\n\n⚠️ Email simulé (SendGrid non configuré)\nL'email sera envoyé quand la clé API sera ajoutée.`);
      } else if (res.data.email_sent) {
        alert(`Devis ${res.data.numero} créé et envoyé par email à ${form.client_email} !`);
      }
      onCreated();
    } catch (err) { setError(err.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="font-bold text-gray-900">Créer un devis</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          {error && <p className="text-red-500 text-sm bg-red-50 p-3">{error}</p>}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Client *</label>
            <ClientSelector value={form.client_id} onChange={handleClientSelect}
              clients={clients} authHeaders={authHeaders} onClientCreated={onRefresh.fetchClients} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Véhicule *</label>
            <select value={form.vehicle_id} onChange={e => handleVehicleChange(e.target.value)} required data-testid="create-vehicle-select"
              className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]">
              <option value="">Choisir un véhicule</option>
              {vehicles.map(v => <option key={v.id} value={v.id}>{v.name} — {v.price}€</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">
              Montant (€) <span className="text-gray-400 text-[10px] normal-case font-normal">— modifiable</span>
            </label>
            <input type="number" value={form.montant} onChange={e => set("montant", parseInt(e.target.value) || 0)}
              data-testid="create-montant" placeholder="Auto-rempli depuis le véhicule"
              className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843] bg-amber-50 font-semibold" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[["client_email","Email *","email"],["client_telephone","Téléphone *","tel"],
              ["client_ville","Ville *","text"]].map(([key, label, type]) => (
              <div key={key}>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">{label}</label>
                <input type={type} value={form[key]} onChange={e => set(key, e.target.value)}
                  required={label.includes("*")} data-testid={`create-${key}`}
                  className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
              </div>
            ))}
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Date mariage *</label>
              <input type="date" value={form.date_mariage} onChange={e => handleDateChange(e.target.value)}
                required data-testid="create-date_mariage"
                className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
            </div>
          </div>
          {conflict && (
            <div className="bg-amber-50 border border-amber-300 rounded p-3" data-testid="conflict-warning">
              <p className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                <AlertTriangle size={16} /> Attention : Conflit de date détecté
              </p>
              <ul className="mt-2 space-y-1">
                {conflict.conflicts.map(c => (
                  <li key={c.id} className="text-xs text-amber-700">
                    • <strong>{c.numero}</strong> - {c.client_name}
                    <span className={`ml-1 px-1.5 py-0.5 rounded ${c.is_reservation ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {c.is_reservation ? 'Réservé' : 'Devis'}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-amber-600 mt-2">Vous pouvez tout de même créer ce devis si nécessaire.</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Lieu événement</label>
              <SearchableSelector value={form.lieu_evenement} onChange={v => set("lieu_evenement", v)}
                items={lieux} placeholder="Sélectionner..." apiEndpoint="lieux"
                authHeaders={authHeaders} onCreated={onRefresh.fetchLieux} icon={MapPin} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Lieu de la cérémonie</label>
              <SearchableSelector value={form.lieu_ceremonie} onChange={v => set("lieu_ceremonie", v)}
                items={lieuxCeremonie} placeholder="Ville église / chapelle..." apiEndpoint="lieux-ceremonie"
                authHeaders={authHeaders} onCreated={onRefresh.fetchLieuxCeremonie} icon={MapPin} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Salle réception</label>
              <SearchableSelector value={form.salle_reception} onChange={v => set("salle_reception", v)}
                items={salles} placeholder="Sélectionner..." apiEndpoint="salles"
                authHeaders={authHeaders} onCreated={onRefresh.fetchSalles} icon={Building} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Thème</label>
              <SearchableSelector value={form.theme_decoration} onChange={v => set("theme_decoration", v)}
                items={themes} placeholder="Sélectionner..." apiEndpoint="themes"
                authHeaders={authHeaders} onCreated={onRefresh.fetchThemes} icon={Tags} />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Champagne</label>
              <select value={form.champagne} onChange={e => set("champagne", e.target.value)}
                className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]">
                {CHAMPAGNE_OPTS.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Notes</label>
            <textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2}
              className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843] resize-none" />
          </div>
          <div className="flex gap-3 pt-2 justify-end border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-5 py-2 border border-gray-200 text-sm font-semibold hover:bg-gray-50">Annuler</button>
            <button type="submit" disabled={loading} data-testid="create-devis-submit"
              className="px-6 py-2 bg-[#1B2E4B] text-white text-sm font-semibold hover:bg-[#162440] disabled:opacity-60">
              {loading ? "Création..." : "Créer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
